import { useEffect, useRef, useState } from 'react';

import {
  type CheckoutCredentials,
  type StudyPayment,
  openCheckout,
  sendFakeCheckoutEvent,
} from '../api/client.ts';
import { isApiError } from '../api/errors.ts';
import { type EmbeddedCheckoutPage, loadStripe } from '../payment/stripe-js.ts';
import { Modal } from '../ui/Modal.tsx';
import type { CheckoutFormActions } from './LaunchPanel.tsx';
import {
  CHECKOUT_FORM_FAILED,
  CHECKOUT_FORM_LOADING,
  CHECKOUT_MODAL_TITLE,
  CHECKOUT_NOT_CONFIGURED,
  CHECKOUT_RETRY,
  CHECKING_CHECKOUT,
  CONTINUE_CHECKOUT,
  CONTINUING_CHECKOUT,
  SHOW_CHECKOUT_FORM,
  TEST_CHECKOUT_EXPIRE,
  TEST_CHECKOUT_FAILED,
  TEST_CHECKOUT_HEADING,
  TEST_CHECKOUT_LINE,
  TEST_CHECKOUT_PAY,
  TEST_CHECKOUT_WAITING,
} from './launch-copy.ts';

export interface CheckoutFormProps {
  studyId: string;
  /**
   * The open session's client secret, as the panel holds it from the answer that opened or
   * resumed the checkout: the form's first ask is answered with it rather than another request.
   * It is handed to Stripe.js alone — never logged, put in an address or stored.
   */
  clientSecret: string | null;
  /** The key Stripe's form is created with; null for the fake provider, which has no form. */
  publishableKey: string | null;
  /**
   * The form says the payment is complete. That is a reason to ask the server, never a payment:
   * only the provider's own report, read by the server, pays the purchase.
   */
  onComplete: () => void;
  /** The server answered that the checkout is no longer open, with the status it is in now. */
  onClosed: (status: StudyPayment) => void;
  /** The fake's checkout was expired from its test box. */
  onExpired: () => void;
  /** The server refused the checkout for good, in words written for the researcher. */
  onRefused: (refusal: { code: string; message: string }) => void;
}

/**
 * What fills Launch's open checkout: the payment form, once the panel holds the credentials the
 * last Pay or Try again was answered with, and otherwise Continue, which asks for them — a
 * checkout another tab opened, or this one before a reload. Either way it is the purchase's one
 * session.
 *
 * The form is shown in a modal, which opens whenever credentials for a new session arrive. Closing
 * the modal hides the form and leaves it mounted — Stripe's frame is created once per form, and
 * the checkout session stays open — so the panel offers to show it again beside Cancel. A
 * refusal from the form closes the modal too: the panel says it in an alert of its own, which a
 * modal left open would cover with a form that has nothing in it.
 */
export const CheckoutSlot = ({
  studyId,
  credentials,
  form,
}: {
  studyId: string;
  credentials: CheckoutCredentials | null;
  form: CheckoutFormActions;
}) => {
  /**
   * The session whose modal was closed, by its client secret: a later answer for another session
   * opens it again, and one for the same session leaves it closed.
   */
  const [closedFor, setClosedFor] = useState<string | null>(null);
  /** Where focus lands when the modal closes: the control that shows it again. */
  const show = useRef<HTMLButtonElement>(null);

  if (credentials === null) {
    return (
      <button
        className="button-primary launch-checkout-open-form"
        data-test-id="launch-checkout-continue"
        disabled={form.busy || form.checking}
        onClick={form.onContinue}
        type="button"
      >
        {form.checking
          ? CHECKING_CHECKOUT
          : form.continuing
            ? CONTINUING_CHECKOUT
            : CONTINUE_CHECKOUT}
      </button>
    );
  }

  const open = closedFor !== credentials.clientSecret;

  return (
    <>
      <button
        className="button-primary launch-checkout-open-form"
        data-test-id="launch-checkout-show"
        onClick={() => setClosedFor(null)}
        ref={show}
        type="button"
      >
        {SHOW_CHECKOUT_FORM}
      </button>
      <Modal
        className="checkout-modal"
        closeTestId="launch-checkout-modal-close"
        onClose={() => setClosedFor(credentials.clientSecret)}
        open={open}
        returnFocus={show}
        testId="launch-checkout-modal"
        title={CHECKOUT_MODAL_TITLE}
      >
        <CheckoutForm
          clientSecret={credentials.clientSecret}
          onClosed={form.onClosed}
          onComplete={form.onComplete}
          onExpired={form.onExpired}
          onRefused={(refusal) => {
            setClosedFor(credentials.clientSecret);
            form.onRefused(refusal);
          }}
          publishableKey={credentials.publishableKey}
          studyId={studyId}
        />
      </Modal>
    </>
  );
};

/** The Launch panel's `checkout` slot for a study: what fills its open checkout. */
export const checkoutSlotFor =
  (studyId: string) => (credentials: CheckoutCredentials | null, form: CheckoutFormActions) => (
    <CheckoutSlot credentials={credentials} form={form} studyId={studyId} />
  );

/**
 * Where an open checkout is paid: Stripe's embedded form, or — in a development build talking to
 * the fake provider, which answers no publishable key — the test box that stands in for it. A
 * production build answered no key has nothing to mount, and says so.
 */
export const CheckoutForm = ({
  studyId,
  clientSecret,
  publishableKey,
  onComplete,
  onClosed,
  onExpired,
  onRefused,
}: CheckoutFormProps) =>
  publishableKey === null ? (
    import.meta.env.DEV ? (
      <TestCheckout studyId={studyId} onComplete={onComplete} onExpired={onExpired} />
    ) : (
      <p className="chat-error" data-test-id="launch-checkout-not-configured" role="alert">
        {CHECKOUT_NOT_CONFIGURED}
      </p>
    )
  ) : (
    <EmbeddedCheckout
      clientSecret={clientSecret}
      studyId={studyId}
      publishableKey={publishableKey}
      onComplete={onComplete}
      onClosed={onClosed}
      onRefused={onRefused}
    />
  );

/**
 * The life of the last embedded form on the page, from asking for it to destroying it. Stripe
 * allows one at a time, so each form waits for the one before it, whichever component made it.
 */
let lastForm: Promise<void> = Promise.resolve();

/**
 * The refusals of a checkout that asking again answers the same — refused and released, opened
 * through another provider, its session gone, or closed — which the panel says in the server's
 * words and the form does not offer to retry.
 */
const FINAL_CHECKOUT_CODES: ReadonlySet<string> = new Set([
  'checkout_failed',
  'checkout_other_provider',
  'checkout_session_missing',
  'checkout_closed',
]);

/**
 * Thrown into Stripe's form when the server has no open session to give it — closed, or refused
 * for good — once the panel has been told; the form then offers nothing of its own.
 */
class CheckoutNotOpenError extends Error {
  constructor() {
    super('This checkout is no longer open.');
    this.name = 'CheckoutNotOpenError';
  }
}

/**
 * Stripe's embedded Checkout form, mounted into this component's element and destroyed with it.
 *
 * Card details are typed into Stripe's frame and never reach Sigora. The form asks for the
 * session's client secret through `…/purchase/checkout` each time it needs one, so the secret is
 * read from the provider then and kept nowhere on this side. An answer with no credentials beside
 * a status that is no longer pending is a checkout that closed meanwhile, which goes back to the
 * panel as the status it is in; beside a pending one it is a session not open yet, which Try again
 * asks for again.
 *
 * Stripe allows one embedded form on a page at a time, so each mount waits for the one before it
 * to be created and destroyed: a form replaced while the last is still being created — another
 * purchase's, a Try again — never has two alive at once.
 */
export const EmbeddedCheckout = ({
  studyId,
  clientSecret,
  publishableKey,
  onComplete,
  onClosed,
  onRefused,
}: Omit<CheckoutFormProps, 'onExpired'> & { publishableKey: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  /** Bumped by Try again, which creates the form afresh. */
  const [attempt, setAttempt] = useState(0);
  /**
   * How the latest attempt ended: mounted, failed in a way Try again may clear, or ended by the
   * server, which the panel says. Loading until it ends.
   */
  const [outcome, setOutcome] = useState<{
    attempt: number;
    ended: 'mounted' | 'failed' | 'refused';
  } | null>(null);
  /** The held secret, which only the form's first ask uses; later asks go to the server. */
  const heldSecretRef = useRef(clientSecret);
  /** Read through a ref so a new callback from the panel does not recreate the form. */
  const callbacksRef = useRef({ onComplete, onClosed, onRefused });
  useEffect(() => {
    callbacksRef.current = { onComplete, onClosed, onRefused };
  });

  useEffect(() => {
    const element = containerRef.current;
    if (element === null) {
      return;
    }

    let cancelled = false;
    let page: EmbeddedCheckoutPage | null = null;
    /** Whether Stripe has been asked to create the form, after which only Stripe's answer ends it. */
    let asked = false;
    /** Whether Stripe has created the form, after which a failed ask for the secret ends it here. */
    let created = false;
    /** Settles once this form is destroyed, or once it is known it never will be created. */
    let release = () => {};
    const destroyed = new Promise<void>((resolve) => {
      release = resolve;
    });

    const life = lastForm.then(async () => {
      if (cancelled) {
        return;
      }

      const stripe = await loadStripe();
      if (cancelled) {
        return;
      }

      /** The server's answer to an ask for the secret, or why it gives the form none. */
      const askServer = async () => {
        const answer = await openCheckout(studyId).catch((failure: unknown) => {
          if (isApiError(failure) && FINAL_CHECKOUT_CODES.has(failure.code)) {
            callbacksRef.current.onRefused({ code: failure.code, message: failure.message });
            throw new CheckoutNotOpenError();
          }

          throw failure;
        });
        if (answer.checkout === null) {
          if (answer.status.kind === 'pending') {
            // Still pending with no session to give the form — one still being opened, or one
            // the provider has not yet settled: asking again may answer it, so Try again does.
            throw new Error('This checkout has no open session yet.');
          }

          callbacksRef.current.onClosed(answer.status);
          throw new CheckoutNotOpenError();
        }

        return answer.checkout.clientSecret;
      };

      asked = true;
      const form = await stripe(publishableKey).createEmbeddedCheckoutPage({
        fetchClientSecret: async () => {
          const held = heldSecretRef.current;
          if (held !== null) {
            heldSecretRef.current = null;

            return held;
          }

          try {
            return await askServer();
          } catch (failure) {
            // Stripe may ask again for a form it already created. Its failure is ended here as
            // creation's would be, and Try again replaces the form; while it is being created
            // Stripe's own answer carries the failure.
            if (created && !cancelled) {
              setOutcome({
                attempt,
                ended: failure instanceof CheckoutNotOpenError ? 'refused' : 'failed',
              });
            }

            throw failure;
          }
        },
        onComplete: () => callbacksRef.current.onComplete(),
      });
      created = true;

      if (cancelled) {
        form.destroy();
        release();

        return;
      }

      page = form;
      form.mount(element);
      setOutcome({ attempt, ended: 'mounted' });
    });

    void life.then(
      () => {
        if (page === null) {
          release();
        }
      },
      (failure: unknown) => {
        release();
        if (!cancelled) {
          setOutcome({
            attempt,
            ended: failure instanceof CheckoutNotOpenError ? 'refused' : 'failed',
          });
        }
      },
    );
    lastForm = destroyed;

    return () => {
      cancelled = true;
      if (page !== null) {
        page.destroy();
        page = null;
        release();
      } else if (!asked) {
        // Nothing was created and nothing will be: the next form need not wait for Stripe.js.
        release();
      }
    };
  }, [studyId, publishableKey, attempt]);

  const settled = outcome?.attempt === attempt ? outcome : null;

  return (
    <div className="launch-checkout-form-wrap">
      {settled === null && (
        <p className="launch-quote-detail" data-test-id="launch-checkout-form-loading">
          {CHECKOUT_FORM_LOADING}
        </p>
      )}

      {settled?.ended === 'failed' && (
        <>
          <p className="chat-error" data-test-id="launch-checkout-form-failed" role="alert">
            {CHECKOUT_FORM_FAILED}
          </p>
          <button
            data-test-id="launch-checkout-form-retry"
            onClick={() => setAttempt((count) => count + 1)}
            type="button"
          >
            {CHECKOUT_RETRY}
          </button>
        </>
      )}

      <div
        className="launch-checkout-form"
        data-test-id="launch-checkout-form"
        ref={containerRef}
      />
    </div>
  );
};

/**
 * The fake provider's stand-in for the payment form: Pay and Expire, each sent through the local
 * fake route as Stripe's form or its expiry would be, and settled by the server as a Stripe
 * payment is. It is offered only where the server answered no publishable key, which is the fake,
 * and the route it calls exists only on a local stack running it.
 */
export const TestCheckout = ({
  studyId,
  onComplete,
  onExpired,
}: Pick<CheckoutFormProps, 'studyId' | 'onComplete' | 'onExpired'>) => {
  const [sending, setSending] = useState<'complete' | 'expire' | null>(null);
  /** Set once an event is sent: the box waits, unusable, until the checkout it is about changes. */
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  const send = async (action: 'complete' | 'expire') => {
    setSending(action);
    setFailed(false);
    try {
      await sendFakeCheckoutEvent(studyId, action);
      setSent(true);
      if (action === 'complete') {
        onComplete();
      } else {
        onExpired();
      }
    } catch {
      setFailed(true);
    } finally {
      setSending(null);
    }
  };

  return (
    <section
      aria-labelledby="launch-test-checkout-heading"
      className="launch-test-checkout"
      data-test-id="launch-test-checkout"
    >
      <h3 className="launch-problems-heading" id="launch-test-checkout-heading">
        {TEST_CHECKOUT_HEADING}
      </h3>
      <p className="launch-quote-detail">{TEST_CHECKOUT_LINE}</p>
      <div className="launch-test-checkout-actions">
        <button
          className="button-primary"
          data-test-id="launch-test-checkout-pay"
          disabled={sending !== null || sent}
          onClick={() => void send('complete')}
          type="button"
        >
          {TEST_CHECKOUT_PAY}
        </button>
        <button
          data-test-id="launch-test-checkout-expire"
          disabled={sending !== null || sent}
          onClick={() => void send('expire')}
          type="button"
        >
          {TEST_CHECKOUT_EXPIRE}
        </button>
      </div>
      {sent && (
        <p className="launch-quote-detail" data-test-id="launch-test-checkout-waiting">
          {TEST_CHECKOUT_WAITING}
        </p>
      )}
      {failed && (
        <p className="chat-error" data-test-id="launch-test-checkout-failed" role="alert">
          {TEST_CHECKOUT_FAILED}
        </p>
      )}
    </section>
  );
};
